// AllergiesSection — embedded in PatientDetailPage after ClinicalCard.
// Shows allergy list with severity badges and active/inactive toggle.
// No separate route — allergies are a short list suited for inline display.

import { useState } from "react";
import type { JSX } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

interface Allergy {
  _id: string; substance: string; reaction_classification: string | null;
  severity: string; onset_date: string | null; notes: string | null; is_active: boolean;
}

interface AllergyPayload {
  substance: string; reaction_classification: string; severity: string;
  onset_date: string; notes: string; is_active: boolean;
}

const REACTIONS  = ["allergy", "intolerance", "sensitivity"] as const;
const SEVERITIES = ["mild", "moderate", "severe", "unknown"] as const;

const SEV_CLS: Record<string, string> = {
  severe:           "bg-red-100 text-red-700",
  life_threatening: "bg-red-200 text-red-800",
  moderate:         "bg-amber-100 text-amber-700",
  mild:             "bg-emerald-100 text-emerald-700",
  unknown:          "bg-slate-100 text-slate-500",
};

const S = "mt-1 block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm";
const L = "block text-xs text-slate-500 mb-0.5";

function AllergyModal({ patientId, onClose }: { patientId: string; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [f, setF] = useState<AllergyPayload>({
    substance: "", reaction_classification: "allergy", severity: "unknown",
    onset_date: "", notes: "", is_active: true,
  });
  const upd = (k: keyof AllergyPayload, v: string | boolean): void => setF((p) => ({ ...p, [k]: v }));

  const { mutate, isPending, isError } = useMutation({
    mutationFn: (p: AllergyPayload) => apiFetch(`/patients/${patientId}/allergies`, {
      method: "POST",
      body: JSON.stringify({ ...p, onset_date: p.onset_date || undefined, notes: p.notes || undefined }),
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["allergies", patientId] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Add Allergy</h2>
        <div className="space-y-3">
          <label className="block"><span className={L}>Substance / Allergen <span className="text-red-500">*</span></span>
            <input required className={S} value={f.substance} onChange={(e) => upd("substance", e.target.value)} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={L}>Reaction type <span className="text-red-500">*</span></span>
              <select required className={S} value={f.reaction_classification} onChange={(e) => upd("reaction_classification", e.target.value)}>
                {REACTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="block"><span className={L}>Severity <span className="text-red-500">*</span></span>
              <select required className={S} value={f.severity} onChange={(e) => upd("severity", e.target.value)}>
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="block"><span className={L}>Onset date (approximate)</span>
            <input type="date" className={S} value={f.onset_date} onChange={(e) => upd("onset_date", e.target.value)} />
          </label>
          <label className="block"><span className={L}>Notes</span>
            <textarea className={S} rows={2} maxLength={200} value={f.notes} onChange={(e) => upd("notes", e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.is_active} onChange={(e) => upd("is_active", e.target.checked)} className="rounded" />
            Active allergy
          </label>
        </div>
        {isError && <p className="text-sm text-red-600">Failed to save. Please try again.</p>}
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">Cancel</button>
          <button disabled={isPending || !f.substance} onClick={() => mutate(f)}
            className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
            {isPending ? "Saving…" : "Add Allergy"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AllergyRow({ a, onToggle }: { a: Allergy; onToggle: () => void }): JSX.Element {
  return (
    <div className={`flex items-center gap-3 py-2 ${!a.is_active ? "opacity-50" : ""}`}>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-slate-800">{a.substance}</span>
        {a.reaction_classification && (
          <span className="ml-2 text-xs text-slate-400">{a.reaction_classification}</span>
        )}
      </div>
      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${SEV_CLS[a.severity] ?? "bg-slate-100 text-slate-500"}`}>
        {a.severity}
      </span>
      <button onClick={onToggle}
        className={`text-xs px-2 py-0.5 rounded-full border shrink-0 ${a.is_active ? "border-emerald-300 text-emerald-600" : "border-slate-200 text-slate-400"}`}>
        {a.is_active ? "Active" : "Inactive"}
      </button>
    </div>
  );
}

export function AllergiesSection({ patientId }: { patientId: string }): JSX.Element {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const { data: allergies, isLoading } = useQuery<Allergy[]>({
    queryKey: ["allergies", patientId],
    queryFn:  () => apiFetch<Allergy[]>(`/patients/${patientId}/allergies`),
  });
  const toggle = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      apiFetch(`/allergies/${id}`, { method: "PATCH", body: JSON.stringify({ is_active }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["allergies", patientId] }),
  });

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Allergies</p>
        <button onClick={() => setShowAdd(true)}
          className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
          + Add
        </button>
      </div>
      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {!isLoading && (!allergies || allergies.length === 0) && (
        <p className="text-sm text-slate-400">No allergies recorded.</p>
      )}
      {allergies && allergies.length > 0 && (
        <div className="divide-y divide-slate-50">
          {allergies.map((a) => (
            <AllergyRow key={a._id} a={a}
              onToggle={() => toggle.mutate({ id: a._id, is_active: !a.is_active })} />
          ))}
        </div>
      )}
      {showAdd && <AllergyModal patientId={patientId} onClose={() => setShowAdd(false)} />}
    </div>
  );
}
